import React from "react";
import { Box, Typography, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Consistent header for a *sub-section* within an admin tab (a chart, a table,
// an eval panel). The tabs previously mixed bare `subtitle1`/`h6` headings with
// drifting weights and no shared anchor, so sections didn't read as one system.
// This gives every section the same treatment — an accent tick + an uppercase
// eyebrow label, an optional right-aligned `action` slot (filter, button), an
// optional `caption`, and an optional hairline divider — so scanning down a tab
// reveals a clean, rhythmic spine of section starts.
//
// The label text is passed through verbatim (the uppercase is CSS-only via
// text-transform), so it stays selectable/matchable by its original casing.
export default function AdminSectionHeader({
  label,
  caption,
  accent,
  action,
  divider = true,
  sx,
  ...rest
}) {
  const theme = useTheme();
  const tick = accent || theme.palette.primary.main;
  return (
    <Box sx={{ mb: 1.5, ...sx }} {...rest}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
        >
          <Box
            aria-hidden
            sx={{
              width: 3,
              height: 16,
              borderRadius: 1,
              backgroundColor: tick,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: theme.palette.text.primary,
              lineHeight: 1.3,
            }}
          >
            {label}
          </Typography>
        </Box>
        {action ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            {action}
          </Box>
        ) : null}
      </Box>
      {caption ? (
        <Typography
          variant="caption"
          component="p"
          sx={{ color: theme.palette.text.secondary, mt: 0.5, ml: "11px" }}
        >
          {caption}
        </Typography>
      ) : null}
      {divider ? <Divider sx={{ mt: 1.25 }} /> : null}
    </Box>
  );
}
